import { useCallback, useEffect, useRef, useState } from "react";

export interface MarqueeRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const MIN_DRAG = 4; // px before a click counts as a drag, not a mis-click

/**
 * Finder/Explorer-style rubber-band selection.
 *
 * Dragging sweeps a rectangle and selects every row it touches. It starts from
 * anywhere EXCEPT an element marked `data-drag-handle` — that's a document's
 * name/icon, where the same gesture means "move this file" instead. So the
 * inert parts of a row (size, dates, editor) are marquee surface too.
 *
 * Coordinates are viewport-relative and the overlay is position:fixed, so
 * scrolling mid-drag needs no offset maths.
 */
export function useMarquee({
  containerRef,
  rowCount,
  onSelect,
}: {
  containerRef: React.RefObject<HTMLElement>;
  rowCount: number;
  /** Indices the band currently covers; `additive` when Cmd/Ctrl was held. */
  onSelect: (indices: number[], additive: boolean) => void;
}) {
  const [rect, setRect] = useState<MarqueeRect | null>(null);
  const origin = useRef<{ x: number; y: number; additive: boolean } | null>(null);
  /** True once the pointer has moved past MIN_DRAG — kept in a ref so the
   *  listeners don't re-bind on every frame of the drag. */
  const dragging = useRef(false);

  /** True if a band was actually swept — read once, then reset. */
  const justDragged = useRef(false);
  const consumeDrag = useCallback(() => {
    const was = justDragged.current;
    justDragged.current = false;
    return was;
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    // Left button only, and never from a drag handle (that gesture is a move)
    // or a control. Pressing anywhere else — including the inert parts of a
    // row — is fair game, so you can sweep a selection starting mid-list.
    if (e.button !== 0) return;
    const el = e.target as HTMLElement;
    if (el.closest("[data-drag-handle]") || el.closest("button, a, input, th")) return;
    origin.current = { x: e.clientX, y: e.clientY, additive: e.metaKey || e.ctrlKey };
  }, []);

  // Listeners stay attached and no-op until a drag actually starts: `origin` is
  // a ref, so setting it on mousedown never re-runs an effect that gates on it.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const o = origin.current;
      if (!o) return;
      const dx = Math.abs(e.clientX - o.x);
      const dy = Math.abs(e.clientY - o.y);
      if (!dragging.current && dx < MIN_DRAG && dy < MIN_DRAG) return;
      dragging.current = true;
      justDragged.current = true;

      const band = {
        left: Math.min(o.x, e.clientX),
        top: Math.min(o.y, e.clientY),
        width: dx,
        height: dy,
      };
      setRect(band);

      // Hit-test against live row geometry — cheap at the row counts a single
      // folder holds, and immune to virtualisation/scroll drift.
      const hits: number[] = [];
      const rows = containerRef.current?.querySelectorAll<HTMLElement>("[data-row-index]");
      rows?.forEach((row) => {
        const r = row.getBoundingClientRect();
        const overlaps =
          r.left < band.left + band.width &&
          r.right > band.left &&
          r.top < band.top + band.height &&
          r.bottom > band.top;
        if (overlaps) hits.push(Number(row.dataset.rowIndex));
      });
      onSelect(hits, o.additive);
    };

    const onUp = () => {
      if (!origin.current) return;
      origin.current = null;
      dragging.current = false;
      setRect(null);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [rowCount, containerRef, onSelect]);

  return { marquee: rect, onMouseDown, consumeDrag };
}

/** The translucent selection band itself. */
export function MarqueeBox({ rect }: { rect: MarqueeRect }) {
  return (
    <div
      className="pointer-events-none fixed z-30 rounded-sm border border-accent"
      style={{
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        background: "var(--color-accent-muted)",
        opacity: 0.5,
      }}
    />
  );
}
